import React, { useEffect, useState } from 'react'
import { Container, Css, ToastContext, Tools } from '@pdrj/pmi-iqos-ds-react'
import {
  CommonTranslations,
  constants,
  loadExternalScript,
  parseTranslations,
  PublishedComponent,
  Routes,
  goToRoute,
  localstorage,
  isAuthenticated,
  cookie
} from '@pdrj/pmi-iqos-shared-react'
import { api } from '@iqosphere-common/services'
import { getInitialPropsMock } from '@iqosphere-common/utils'
import SpotsTranslations from './spots.json'
import SpotsMapView from './spots-map-view'
import SpotsListView from './spots-list-view'
import mapStyles from './map-styles'
import SpotsVenueDetail from './spots-venue-detail'

const ViewTypes = {
  MAP: 'map',
  LIST: 'list',
  DETAIL: 'detail'
}

const userDefaultLocation = {
  lat: 35.672698,
  lng: 139.736464
}
const mapConfig = {
  radius: 5000,
  offset: 0,
  limit: 50
}
const mapStylingRules = mapStyles
const mapDefaultViewSettings = {
  zoom: 14,
  minZoom: 6,
  disableDefaultUI: true,
  gestureHandling: 'greedy',
  zoomControl: false,
  restriction: {
    latLngBounds: {
      north: 47,
      south: 26,
      east: 150,
      west: 120
    }
  }
}
const mapDomId = 'maps'

const debounceDelay = 1000

const SpotsComponent = ({
  translations,
  spotFilters,
  spotBusinessTypes,
  spotUsageTypes,
  containerClass,
  view,
  venueId
}) => {
  const [consumerType, setConsumerType] = useState('')
  const filterTypes = (spotFilters && Object.keys(spotFilters)) || []

  const initConsumerType = () => {
    if (isAuthenticated()) {
      const tokenInfo = cookie.getTokenInfo()
      if (tokenInfo) {
        const type = tokenInfo.isAV ? 'av_only' : 'av_asd_only'
        setConsumerType(type)
      }
    }
  }
  const resetPage = () => {
    initConsumerType()
    initMap()
  }

  React.useEffect(() => {
    initConsumerType()
  }, [])

  const buildFilterState = () => {
    const buildFilterTypeState = (filterList, type) => {
      return filterList.map(filter => {
        return { ...filter, type }
      })
    }
    let result = []
    filterTypes.forEach(filterType => {
      result = result.concat(
        buildFilterTypeState(spotFilters[filterType], filterType)
      )
    })
    const storedFiltersSelection = localstorage.get('SpotFiltersSelection')
    if (storedFiltersSelection) {
      result.forEach(f => (f.active = storedFiltersSelection[f.id]))
    }
    return result.filter(
      filter =>
        filter.access === 'av_asd_only' ||
        filter.access === consumerType ||
        filter.access === '' ||
        !filter.access
    )
  }

  const getPageToShow = () => {
    switch (view) {
      case ViewTypes.MAP:
        return 0
      case ViewTypes.LIST:
        return 1
      case ViewTypes.DETAIL:
        return 2
      default:
        return 0
    }
  }

  const [filters, setFilters] = useState(buildFilterState())
  const [pageView, setPageView] = useState(getPageToShow())
  const [spots, setSpots] = useState([])
  const [map, setMap] = useState(null)
  const [userCurrentLocation, setUserCurrentLocation] =
    useState(userDefaultLocation)
  const [userLocationSearched, setUserLocationSearched] =
    useState(userCurrentLocation)
  const [isLoading, setIsLoading] = useState(false)
  const [userLocation, setUserLocation] = useState(undefined)
  const [spotSelected, setSpotSelected] = useState({})
  const [hasErrors, setHasErrors] = useState(false)

  const popupToast = ToastContext.getContextActions().popupToast

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const listenLoginSuccessEvent = (kill = false) => {
    const e = constants.REACT_LOGIN_SUCCESS_EVENT
    const f = resetPage
    if (kill) {
      window.removeEventListener(e, f)
    } else {
      window.addEventListener(e, f)
    }
  }

  useEffect(() => {
    listenLoginSuccessEvent()
    return () => {
      listenLoginSuccessEvent(true)
    }
  }, [listenLoginSuccessEvent])

  React.useEffect(() => {
    setFilters(buildFilterState())
  }, [consumerType])

  const mapData = {
    ...mapConfig,
    lat: userLocationSearched.lat,
    lng: userLocationSearched.lng
  }

  const buildFilterData = (filterList, type) => {
    return (
      (filterList &&
        filterList
          .filter(filter => filter.type === type)
          .filter(filter => filter.active)
          .map(filter => filter.id)
          .join(',')) ||
      ''
    )
  }

  const getSpotsOrderByDistance = spots => {
    return (
      spots &&
      spots
        .map(spot => {
          const origin = userCurrentLocation
          // eslint-disable-next-line no-undef
          const destination = new google.maps.LatLng(spot.lat, spot.lng)
          const distance =
            // eslint-disable-next-line no-undef
            google.maps.geometry.spherical.computeDistanceBetween(
              origin,
              destination
            )
          return { ...spot, distance: (distance / 1000).toFixed(2) }
        })
        .sort((a, b) => a.distance - b.distance)
    )
  }

  const loadData = async (data, filterList) => {
    setIsLoading(true)
    const filtering = {}
    filterTypes.forEach(
      filterType =>
        (filtering[filterType] = buildFilterData(filterList, filterType))
    )
    try {
      const res = await api.iqosphereSpots.getSpots({
        ...data,
        ...filtering
      })
      setSpots(getSpotsOrderByDistance(res.spots))
    } catch (err) {
      setSpots([])
      popupToast({
        type: ToastContext.types.error,
        title: `${translations.tk_errorLoadingSpots}`
      })
    }
  }

  const loadSpotDetail = async () => {
    setIsLoading(true)
    try {
      const res = await api.iqosphereSpots.getSpotDetail(venueId)
      setSpotSelected(res)
    } catch (ex) {
      setHasErrors(true)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (venueId) {
      loadSpotDetail()
    } else {
      setHasErrors(true)
    }
  }, [])

  useEffect(() => {
    if (view !== ViewTypes.DETAIL) {
      window.initMap = initMap
      loadExternalScript(
        'mapScript',
        `https://maps.googleapis.com/maps/api/js?key=${constants.GOOGLE_MAPS_API_KEY}&libraries=geometry&callback=initMap`,
        () => {}
      )
    }
  }, [])

  const initMap = reset => {
    const storedMapDataInfo = localstorage.get('SpotMapDataInfo')
    let userCoords = userCurrentLocation

    // eslint-disable-next-line no-undef
    const map = new google.maps.Map(document.getElementById(mapDomId), {
      styles: mapStylingRules,
      ...mapDefaultViewSettings,
      center:
        !reset && storedMapDataInfo
          ? {
              lat: storedMapDataInfo.lat,
              lng: storedMapDataInfo.lng
            }
          : userCoords,
      ...(!reset && storedMapDataInfo && { zoom: storedMapDataInfo.zoom })
    })

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => {
          userCoords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          }
          setUserCurrentLocation(userCoords)
          setUserLocation(userCoords)
          map.setCenter(userCoords)
          setMap(map)
        },
        () => {
          // The Geolocation service failed
          setMap(map)
        }
      )
    } else {
      setMap(map)
    }
  }

  useEffect(() => {
    if (isLoading && view !== ViewTypes.DETAIL) {
      loadData(mapData, filters)
    }
  }, [filters])

  const handleFilterToggle = toggledFilter => {
    const newFilters = filters.map(f => {
      return f.id === toggledFilter.id ? { ...f, active: !f.active } : { ...f }
    })
    const filtersSelection = {}
    newFilters.forEach(f => (filtersSelection[f.id] = f.active))
    localstorage.set('SpotFiltersSelection', filtersSelection)
    setFilters(newFilters)
  }

  const handleGoCurrentLocation = () => {
    initMap(true)
  }

  const handleOpenVenueDetail = spot => () => {
    goToRoute({
      mainPath: `${Routes.spotListPage}/${spot.spotId}`
    })
  }

  return (
    <>
      <Container
        display={
          pageView === 0 ? Container.displays.block : Container.displays.none
        }>
        <Css
          s={{
            height: Tools.isDesktop()
              ? 'calc(100dvh - 76px)'
              : 'calc(100dvh - 56px)'
          }}
          classname={containerClass}>
          <SpotsMapView
            translations={translations}
            filters={filters}
            spots={spots}
            map={map}
            businessTypes={spotBusinessTypes}
            usageTypes={spotUsageTypes}
            showListPage={() => {
              setPageView(1)
            }}
            toggleFilterHandler={handleFilterToggle}
            handleGoCurrentLocation={handleGoCurrentLocation}
            loadData={loadData}
            mapConfig={mapConfig}
            mapDomId={mapDomId}
            setUserLocationSearched={setUserLocationSearched}
            debounceDelay={debounceDelay}
            userLocation={userLocation}
          />
        </Css>
      </Container>
      <Container
        display={
          pageView === 1 ? Container.displays.block : Container.displays.none
        }>
        <SpotsListView
          translations={translations}
          spots={spots}
          businessTypes={spotBusinessTypes}
          usageTypes={spotUsageTypes}
          backToMap={() => {
            setPageView(0)
          }}
          handleOpenVenueDetail={handleOpenVenueDetail}
          filters={filters}
          toggleFilterHandler={handleFilterToggle}
        />
      </Container>
      <Container
        display={
          pageView === 2 ? Container.displays.block : Container.displays.none
        }>
        <SpotsVenueDetail
          spotId={venueId}
          translations={translations}
          usageTypes={spotUsageTypes}
          businessTypes={spotBusinessTypes}
          spot={spotSelected}
          backToList={() => {
            goToRoute({
              mainPath: Routes.spotListPage
            })
          }}
          filters={filters}
          hasErrors={hasErrors}
          loading={isLoading}
        />
      </Container>
    </>
  )
}

const Spots = props => {
  return (
    <ToastContext>
      <PublishedComponent
        innerComponent={SpotsComponent}
        {...ComponentDefaultProps}
        {...props}
      />
    </ToastContext>
  )
}

const ComponentTranslations = {
  ...SpotsTranslations,
  ...CommonTranslations
}

const ComponentDefaultProps = {
  componentName: 'Spots',
  translations: {
    ...parseTranslations(ComponentTranslations, true)
  }
}

Spots.getInitialProps = async req => {
  return PublishedComponent.getGlobalInitialProps({
    context: req && req.query && req.query.context,
    componentName: ComponentDefaultProps.componentName,
    defaultTranslations: ComponentDefaultProps.translations,
    translationsConfig: ComponentTranslations,
    getSpecificInitialProps: async () => {
      const venueId = req.query.venueid
      let res = null

      if (venueId) {
        try {
          res = await api.iqosphereSpots.getSpotDetail(venueId)
        } catch (err) {}
      }

      const spotFilters =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('spotFilters')
          : (req.body.extra && req.body.extra.categories.spotsFilters) || ''
      const spotBusinessTypes =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('spotBusinessTypes')
          : (req.body.extra && req.body.extra.categories.spotBusinessTypes) ||
            ''
      const spotUsageTypes =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('spotUsageTypes')
          : (req.body.extra && req.body.extra.categories.spotUsageTypes) || ''
      const containerClass =
        req && req.query && req.query.mock === 'true'
          ? 'q-h-screen-navbar'
          : (req.body.extra && req.body.extra.containerClass) || ''
      const view =
        req && req.query && req.query.mock === 'true'
          ? 'map'
          : (req.body.extra && req.body.extra.context) || 'map'

      return {
        extras: {
          name: (res && res.spotName) || 'Spot Venue',
          description: (res && res.description) || 'Spot Venue'
        },
        spotFilters,
        spotBusinessTypes,
        spotUsageTypes,
        containerClass,
        view: venueId ? ViewTypes.DETAIL : view,
        venueId
      }
    }
  })
}

export default Spots
